import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import { DAY_LABELS } from "@how/website-core";
import {
  useSaveCampus,
  useSetCampusActive,
  type CampusFields,
  type CampusRow,
  type ServiceTime,
} from "./campus-queries";

const DAYS = Object.entries(DAY_LABELS).map(([value, label]) => ({ value, label }));

const EMPTY: CampusFields = {
  name: "",
  slug: "",
  address: "",
  phone: "",
  mapUrl: "",
  timeZone: "",
  serviceTimes: [],
  sortOrder: 0,
  description: "",
  isListed: true,
};

const NEW_SERVICE: ServiceTime = { name: "Sunday worship", day: "sunday", time: "10:00" };

export interface CampusModalProps {
  /** null to add a campus, a row to edit it; undefined when closed. */
  readonly campus: CampusRow | null | undefined;
  readonly canManage: boolean;
  /** How many campuses are active, so the dialog can say why the last one cannot be archived. */
  readonly activeCount: number;
  readonly onClose: () => void;
}

/** One dialog for adding and editing a campus, so the two cannot drift apart. */
export function CampusModal({ campus, canManage, activeCount, onClose }: CampusModalProps) {
  const save = useSaveCampus();
  const setActive = useSetCampusActive();
  // The page remounts this dialog per campus through its key, so the form starts from the campus being opened.
  const [fields, setFields] = useState<CampusFields>(
    campus === null || campus === undefined
      ? EMPTY
      : {
          name: campus.name,
          slug: campus.slug,
          address: campus.addressLines.join("\n"),
          phone: campus.phone ?? "",
          mapUrl: campus.mapUrl ?? "",
          timeZone: campus.timeZone ?? "",
          serviceTimes: campus.serviceTimes,
          sortOrder: campus.sortOrder,
          description: campus.description ?? "",
          isListed: campus.isListed,
        },
  );
  const [confirming, setConfirming] = useState(false);
  const set = (key: keyof CampusFields) => (event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value;
    setFields((current) => ({ ...current, [key]: value }));
  };
  const setService = (index: number, patch: Partial<ServiceTime>) => {
    setFields((current) => ({
      ...current,
      serviceTimes: current.serviceTimes.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  };

  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "Another campus already uses that address."
      : code === "23514"
        ? save.error?.message.includes("time zone")
          ? "That time zone is not one the system knows. Use a name such as America/Chicago."
          : "Check the service times: each needs a name, a day and a time such as 10:00."
        : save.isError
          ? "The campus was not saved. Check your access and try again."
          : null;
  const valid =
    fields.name.trim().length > 0 &&
    fields.serviceTimes.every(
      (s) => s.name.trim().length > 0 && /^([01]\d|2[0-3]):[0-5]\d$/.test(s.time),
    );
  const isLastActive = campus?.isActive === true && activeCount <= 1;

  return (
    <>
      <Modal
        opened={campus !== undefined}
        onClose={onClose}
        title={campus === null ? "Add a campus" : "Edit campus"}
        radius="lg"
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            disabled={!canManage}
            value={fields.name}
            onChange={set("name")}
          />
          <Textarea
            label="Address"
            description="One line per line, as it should print. Leave empty for a campus without a fixed address."
            disabled={!canManage}
            value={fields.address}
            onChange={set("address")}
            autosize
            minRows={2}
            maxRows={4}
          />
          <Group grow align="flex-start">
            <TextInput
              label="Phone"
              disabled={!canManage}
              value={fields.phone}
              onChange={set("phone")}
            />
            <TextInput
              label="Map link"
              description="An https:// address."
              disabled={!canManage}
              value={fields.mapUrl}
              onChange={set("mapUrl")}
            />
          </Group>
          <Group grow align="flex-start">
            <TextInput
              label="Time zone"
              description="Leave empty to use the church's. Otherwise a name such as America/Chicago."
              disabled={!canManage}
              value={fields.timeZone}
              onChange={set("timeZone")}
            />
            <NumberInput
              label="Order"
              description="Lower comes first on the website."
              disabled={!canManage}
              value={fields.sortOrder}
              min={0}
              max={999}
              onChange={(value) => {
                setFields((current) => ({
                  ...current,
                  sortOrder: typeof value === "number" ? value : 0,
                }));
              }}
            />
          </Group>

          <Textarea
            label="Description"
            description="What a visitor reads on this campus's page on your website: where to park, where children check in, which door to use. Blank lines make paragraphs."
            disabled={!canManage}
            value={fields.description}
            onChange={set("description")}
            maxLength={2000}
            autosize
            minRows={3}
          />
          <Switch
            label="Listed in the website's campus chooser"
            description="Off, the campus's page is reachable by its link only, for example before it launches."
            disabled={!canManage}
            checked={fields.isListed}
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              setFields((current) => ({ ...current, isListed: checked }));
            }}
          />

          <Group justify="space-between" align="center" mt="xs">
            <Title order={5}>Service times</Title>
            {canManage && fields.serviceTimes.length < 12 ? (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => {
                  setFields((current) => ({
                    ...current,
                    serviceTimes: [...current.serviceTimes, NEW_SERVICE],
                  }));
                }}
              >
                Add a service
              </Button>
            ) : null}
          </Group>
          {fields.serviceTimes.length === 0 ? (
            <Text size="sm" c="dimmed">
              No services yet. These are what the website's service times show for this campus.
            </Text>
          ) : (
            fields.serviceTimes.map((s, index) => (
              <Group key={index} align="flex-end" wrap="nowrap" gap="xs">
                <TextInput
                  label={index === 0 ? "Service" : undefined}
                  aria-label="Service name"
                  style={{ flex: 2 }}
                  disabled={!canManage}
                  value={s.name}
                  onChange={(event) => {
                    setService(index, { name: event.currentTarget.value });
                  }}
                />
                <Select
                  label={index === 0 ? "Day" : undefined}
                  aria-label="Day"
                  style={{ flex: 1.4 }}
                  disabled={!canManage}
                  data={DAYS}
                  value={s.day}
                  allowDeselect={false}
                  onChange={(value) => {
                    const day = DAYS.find((d) => d.value === value)?.value;
                    if (day !== undefined) setService(index, { day: day as ServiceTime["day"] });
                  }}
                />
                <TextInput
                  label={index === 0 ? "Time" : undefined}
                  aria-label="Time"
                  type="time"
                  style={{ flex: 1 }}
                  disabled={!canManage}
                  value={s.time}
                  onChange={(event) => {
                    setService(index, { time: event.currentTarget.value });
                  }}
                />
                <TextInput
                  label={index === 0 ? "Note" : undefined}
                  aria-label="Note"
                  placeholder="Kids ministry available"
                  style={{ flex: 2 }}
                  disabled={!canManage}
                  value={s.note ?? ""}
                  onChange={(event) => {
                    const note = event.currentTarget.value;
                    setService(index, note.length === 0 ? { note: undefined } : { note });
                  }}
                />
                {canManage ? (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Remove this service"
                    onClick={() => {
                      setFields((current) => ({
                        ...current,
                        serviceTimes: current.serviceTimes.filter((_, i) => i !== index),
                      }));
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                ) : null}
              </Group>
            ))
          )}

          {errorText !== null ? (
            <Text size="sm" c="red">
              {errorText}
            </Text>
          ) : null}

          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              {campus !== null && campus !== undefined && canManage ? (
                campus.isActive ? (
                  <Button
                    variant="subtle"
                    color="red"
                    disabled={isLastActive}
                    title={isLastActive ? "A church needs at least one active campus." : undefined}
                    onClick={() => {
                      setConfirming(true);
                    }}
                  >
                    Archive
                  </Button>
                ) : (
                  <Button
                    variant="subtle"
                    loading={setActive.isPending}
                    onClick={() => {
                      setActive.mutate(
                        { campusId: campus.id, isActive: true },
                        { onSuccess: onClose },
                      );
                    }}
                  >
                    Restore
                  </Button>
                )
              ) : null}
            </Group>
            {canManage ? (
              <Button
                disabled={!valid}
                loading={save.isPending}
                onClick={() => {
                  save.mutate({ campusId: campus?.id ?? null, fields }, { onSuccess: onClose });
                }}
              >
                {campus === null ? "Add campus" : "Save campus"}
              </Button>
            ) : null}
          </Group>
        </Stack>
      </Modal>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Archive campus"
        message={
          campus === null || campus === undefined
            ? ""
            : campus.peopleCount === 0
              ? `Archive ${campus.name}? It leaves the website and the lists; nobody calls it home.`
              : `Archive ${campus.name}? It leaves the website and the lists. ${String(campus.peopleCount)} ${campus.peopleCount === 1 ? "person keeps" : "people keep"} it as their home campus until someone changes it.`
        }
        confirmLabel="Archive campus"
        danger
        loading={setActive.isPending}
        onConfirm={() => {
          if (campus === null || campus === undefined) return;
          setActive.mutate(
            { campusId: campus.id, isActive: false },
            {
              onSuccess: () => {
                setConfirming(false);
                onClose();
              },
            },
          );
        }}
      />
    </>
  );
}
